import React, {
  MouseEvent,
} from 'react';
import IconButton from '@mui/material/IconButton';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import Tooltip from 'core/components/Tooltip';
import DeleteIcon from 'core/icons/delete';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useDeleteChargePolicy,
} from 'pages/Dashboard/pages/Billing/hooks';
import clsx from 'clsx';
import {
  ChargeMasterDTO,
  CustomChargeMasterDTO,
} from 'dtos';
import AmountTag from 'pages/Dashboard/pages/Billing/AmountTag';

type ActionCellProps = {
  isCustom: boolean;
  id: number;
};

function ActionCell({ id, isCustom }: ActionCellProps) {
  const {
    mutateAsync: deleteChargePolicy,
  } = useDeleteChargePolicy();

  const { confirm, closeConfirm } = useConfirmDialog();
  const notify = useNotify();
  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      content: 'Are you sure you want to delete this charge policy?',
      onConfirm: async () => {
        await deleteChargePolicy({
          id,
          isCustom,
        });
        closeConfirm();
        billingEventChannel.emit('onPricesChanged');
        notify({
          message: 'Charge Policy deleted successfully.',
          variant: 'success',
        });
      },
      title: 'Delete Charge Policy',
      confirmText: 'Confirm',
    });
  };
  return (
    <Tooltip title="Delete Price">
      <IconButton
        aria-label="Delete Charge-Policy"
        size="small"
        className="!p-1"
        onClick={handleDelete}
        color="error"
      >
        <DeleteIcon className="!w-5 !h-5 !text-black" />
      </IconButton>
    </Tooltip>
  );
}

type ServiceTextCellProps = {
  value: string;
  header?: string;
  classes?: string;
  maxLength?: number;
};

function ServiceTextCell({
  value,
  header,
  maxLength = 200,
  classes = '',
}: ServiceTextCellProps) {
  return (
    <div className="overflow-hidden flex items-end">
      <Tooltip
        title={(
          <div>
            <div className="font-semibold text-sm">
              {header}
            </div>

            <p>{value}</p>
          </div>
    )}
        classes={{ tooltip: '!w-176 !max-h-[320px] overflow-auto !max-w-[initial]' }}
        hide={value.length <= maxLength}
        isInteractive
      >
        <span className={clsx('max-w-full', classes)}>
          {value?.substring(0, maxLength) ?? ''}
          {value?.length > maxLength ? '...' : null}
        </span>
      </Tooltip>
    </div>
  );
}

export const cptColumns: ColumnDef<ChargeMasterDTO>[] = [{
  id: 'code',
  header: 'Code',
  size: 60,
  className: 'mt-2',
  cell: ({ row }) => (
    <div className="overflow-hidden flex items-end">
      <span className="truncate max-w-full font-semibold inline-block">
        {row.original.billingCode?.code ?? ''}
      </span>
    </div>
  ),
}, {
  id: 'description',
  header: 'Description',
  className: 'mt-1',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <ServiceTextCell
      value={row.original?.billingCode?.description ?? ''}
      maxLength={400}
      header={row.original?.billingCode?.code ?? ''}
    />
  ),
}, {
  id: 'price',
  size: 120,
  header: 'Price',
  className: 'mt-2',
  cell: ({ row }) => (
    <AmountTag value={row.original?.chargeAmount} />
  ),
}, {
  id: 'actions',
  header: '',
  size: 48,
  cell: ({ row }) => (<ActionCell id={row.original?.chargeMasterId ?? 0} isCustom={false} />),
}];

export const customServicesColumns: ColumnDef<CustomChargeMasterDTO>[] = [{
  id: 'serviceName',
  header: 'Service',
  size: 180,
  className: 'mt-1',
  cell: ({ row }) => (
    <ServiceTextCell
      value={row.original?.customServiceCode?.serviceName ?? ''}
      maxLength={200}
      classes="font-semibold"
    />
  ),
}, {
  id: 'description',
  header: 'Description',
  className: 'mt-1',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <ServiceTextCell
      value={row.original?.customServiceCode?.description ?? ''}
      header={row.original?.customServiceCode?.serviceName ?? ''}
      maxLength={400}
    />
  ),
}, {
  id: 'price',
  size: 120,
  header: 'Price',
  className: 'mt-2',
  cell: ({ row }) => (
    <AmountTag value={row.original?.chargeAmount} />
  ),
}, {
  id: 'actions',
  header: '',
  size: 48,
  cell: ({ row }) => (
    <ActionCell id={row.original?.customChargeMasterId ?? 0} isCustom />
  ),
}];
